/**
 * The `reader.selection` slot (UX-SPEC §17, BENCH-UX-SPEC §5): a floating "Rewrite selection" button on a text
 * selection in the Reader view. A plain `selectionchange` listener, independent of `VirtualReader`'s virtualised rows
 * (BENCH-UX-SPEC §3) — it reads each row's `data-p` (paragraph index) to find the selected paragraph range.
 */
import { useEffect, useState } from 'preact/hooks';
import { BENCH } from '../strings-bench.ts';
import { startRewrite } from './controller.ts';

function paraOf(node: Node | null): number | null {
	let el: Element | null = node instanceof Element ? node : (node?.parentElement ?? null);
	while (el) {
		const p = el.closest('.rd-row');
		if (p instanceof HTMLElement && p.dataset.p !== undefined) return Number(p.dataset.p);
		el = el.parentElement;
	}
	return null;
}

export function SelectionAction() {
	const [range, setRange] = useState<{
		paraStart: number;
		paraEnd: number;
		x: number;
		y: number;
	} | null>(null);
	useEffect(() => {
		const onChange = () => {
			const sel = document.getSelection();
			if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
				setRange(null);
				return;
			}
			const a = paraOf(sel.anchorNode);
			const b = paraOf(sel.focusNode);
			if (a === null || b === null) {
				setRange(null);
				return;
			}
			const rect = sel.getRangeAt(0).getBoundingClientRect();
			setRange({
				paraStart: Math.min(a, b),
				paraEnd: Math.max(a, b) + 1,
				x: rect.right + window.scrollX,
				y: rect.top + window.scrollY,
			});
		};
		document.addEventListener('selectionchange', onChange);
		return () => document.removeEventListener('selectionchange', onChange);
	}, []);
	if (!range) return null;
	return (
		<button
			type="button"
			class="btn btn-primary bench-selection-action bench-desktop-only"
			data-testid="reader-rewrite-selection"
			style={{ position: 'absolute', left: `${range.x}px`, top: `${range.y}px` }}
			onClick={() => {
				startRewrite({ kind: 'selection', paraStart: range.paraStart, paraEnd: range.paraEnd });
				setRange(null);
			}}
		>
			{BENCH.rewriteSelection}
		</button>
	);
}
